import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImage } from "@insite/mobius/index";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    fileUpload = "fileUpload",
    label = "label",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
    emailInfo = "emailInfo",
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    textColor = "textColor",
    buttonType = "buttonType",
    paddingAllSide = "paddingAllSide",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.fileUpload]: string;
        [fields.label]: string;
        [fields.emailInfo]: string;
        [fields.openInNewWidow]: boolean;
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.textColor]: string;
        [fields.paddingAllSide]: string;
        [fields.buttonType]: "Page" | "File";
    };
}

type Props = OwnProps;

export interface SurStaticInfoStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: InjectableCss;
    descStyles?: InjectableCss;
    ctaLink?: LinkProps;
    emailInfo?: InjectableCss;
}
export const surStaticInfoStyles: SurStaticInfoStyles = {
    title: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 300;
            font-size: 28px;
            line-height: 36px;
            text-align: center;
            text-transform: capitalize;
            color: ${getColor("text.main")};
            p, span, div {
                font-family: inherit !important;
                color: inherit !important;
            }
        `,
    },
    descStyles: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            p, span, div {
                font-family: inherit !important;
                color: inherit !important;
            }
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 30px;
            padding: 10px 34px;
            background: ${getColor("primary")};
        `,
    },
};

const styles = surStaticInfoStyles;

const SurStaticInfo: React.FC<Props> = ({ fields, id }) => {
    const { url } = useGetLink(fields.link);
    const isMgbw = isMgbwWebsite();
    const secondaryBtn = (ELEMENTS.button as any).secondary;

    const container = {
        css: css`
            // padding: 52px 0px 86px 0px;
            max-width: 955px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            row-gap: 40px;
            padding: ${fields.paddingAllSide};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    };
    const ctaLink = {
        css: css`
            outline: 0;
            background-color: ${isMgbw ? secondaryBtn.background : fields.backgroundColor};
            border: ${isMgbw ? secondaryBtn.borderWidth : "1px"} solid
                ${isMgbw ? secondaryBtn.borderColor : fields.borderColor};
            padding: ${isMgbw ? ELEMENTS.button.base.padding : "10px 34px"};
            width: max-content;
            max-width: 100%;
            display: block;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    null,
                    null,
                ])}
            span {
                display: block;
                text-align: center;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: ${isMgbw ? `${secondaryBtn.color} !important` : `${fields.textColor} !important`};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                font-style: normal !important;
            }
            ${isMgbw
                ? css`
                      &:hover {
                          background-color: ${secondaryBtn.hover.background};
                          border-color: ${secondaryBtn.hover.borderColor};
                          span {
                              color: ${secondaryBtn.hover.color} !important;
                          }
                      }
                  `
                : ""}
        `,
    };

    return (
        <StyledWrapper {...container}>
            {fields.title && <StyledWrapper {...styles.title}>{parse(fields.title, parserOptions)}</StyledWrapper>}
            {fields.desc && <StyledWrapper {...styles.descStyles}>{parse(fields.desc, parserOptions)}</StyledWrapper>}

            {fields.emailInfo && (
                <StyledWrapper {...styles.emailInfo}>{parse(fields.emailInfo, parserOptions)}</StyledWrapper>
            )}

            {fields.buttonType === "Page" && fields.label && fields.link.value ? (
                <Link
                    href={url}
                    target={fields.openInNewWidow ? `_blank` : `_self`}
                    aria-label={fields.label || "Link"}
                    {...ctaLink}
                >
                    {fields.label}
                </Link>
            ) : null}
            {fields.buttonType === "File" && fields.fileUpload && (
                <Link
                    href={fields.fileUpload}
                    download={true}
                    target={fields.openInNewWidow ? `_blank` : `_self`}
                    {...ctaLink}
                    aria-label={fields.label || "Link"}
                >
                    {fields.label}
                </Link>
            )}
        </StyledWrapper>
    );
};

const SurStaticInfoWidget: WidgetModule = {
    component: SurStaticInfo,
    definition: {
        group: "Common",
        displayName: "SurStaticInfo",
        fieldDefinitions: [
            {
                name: fields.paddingAllSide,
                displayName: "padding",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                fieldType: "Translatable",
                name: fields.emailInfo,
                editorTemplate: "RichTextField",
                displayName: "Email Info",
                defaultValue: "",
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.buttonType,
                displayName: "Button Type",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Page", value: "Page" },
                    { displayName: "File", value: "File" },
                ],
                defaultValue: "",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isVisible: item => item.fields[fields.buttonType] === "Page",
            },
            {
                fieldType: "General",
                name: fields.fileUpload,
                editorTemplate: "ImagePickerField",
                displayName: "File Upload",
                defaultValue: "",
                isVisible: item => item.fields[fields.buttonType] === "File",
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurStaticInfoWidget;
